// Selectors for the error markers our form controls render (InputField, Select,
// Checkbox, DatePickerWrapper, and the Tailwind `border-[var(--primary-danger)]` variant).
// Field-agnostic on purpose: whichever errored control sits highest in the
// document wins, so adding new fields needs no change here.
const ERROR_SELECTORS = [
  ".inputfield-input-error",
  ".inputfield-error-message",
  ".questionnaire-select-error-container",
  ".checkbox-error-logo",
  ".date-picker-error",
  '[class*="primary-danger"]',
].join(",");

const FOCUSABLE = "input,select,textarea";

/**
 * Scrolls to the first visible field showing a validation error.
 *
 * @param {HTMLElement|{current: HTMLElement}|null} [container] - scope to search
 *   in; defaults to the whole document.
 * @returns {HTMLElement|null} the element scrolled to, or null if none errored.
 */
export function scrollToFirstError(container) {
  const root = (container && container.current) || container || document;
  if (!root || typeof root.querySelectorAll !== "function") {
    return null;
  }

  const first = Array.from(root.querySelectorAll(ERROR_SELECTORS))
    .filter((element) => element.getClientRects().length > 0)
    .sort(
      (a, b) =>
        a.getBoundingClientRect().top - b.getBoundingClientRect().top ||
        a.getBoundingClientRect().left - b.getBoundingClientRect().left
    )[0];

  if (!first) {
    return null;
  }

  first.scrollIntoView({ behavior: "smooth", block: "center" });

  const focusTarget = first.matches(FOCUSABLE)
    ? first
    : first.querySelector(FOCUSABLE) ||
      (first.closest("div") && first.closest("div").querySelector(FOCUSABLE));
  if (focusTarget && !focusTarget.disabled) {
    focusTarget.focus({ preventScroll: true });
  }

  return first;
}

/**
 * Same as scrollToFirstError, but waits for React to paint the error state
 * first (validation sets state, the error markup only exists after commit).
 */
export function scrollToFirstErrorAfterRender(container) {
  const raf =
    typeof window !== "undefined" && window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (callback) => setTimeout(callback, 0);

  raf(() => raf(() => scrollToFirstError(container)));
}

export default scrollToFirstError;
